"use client";
import { useMemo, useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { Icons } from "@/components/Icons";
import { Badge, Button, Card, TopBar } from "@/components/ui";
import { FoodImg, type Tone } from "@/components/FoodImg";
import { SignInModal } from "@/components/SignInModal";
import { useAuth } from "@/store/auth";
import { useOrders } from "@/hooks/useOrders";
import { useFormatPrice } from "@/hooks/useFormatPrice";
import { Price } from "@/components/Price";
import { reviewApi, canAttemptReview } from "@/api/reviewApi";
import type { ReviewEligibility } from "@/api/reviewApi";
import { StarRating } from "@/components/reviews/StarRating";
import { useCart, type CartLine } from "@/store/cart";
import { useMenu, localize, type UiMenuItem } from "@/hooks/useMenu";
import {
  OrderStatusEnum,
  PaymentStatusEnum,
  OrderTypeEnum,
  type OrderOutput,
  type OrderLineOutput,
  type SelectedModifierOption,
  type SelectedVariantOption,
} from "@/api/online-ordering-service/model";

const TONES: Tone[] = ["neutral", "rose", "honey", "amber"];

type Tab = "active" | "past" | "drafts";

const TERMINAL_STATUSES: OrderStatusEnum[] = [
  OrderStatusEnum.Delivered,
  OrderStatusEnum.Completed,
  OrderStatusEnum.Cancelled,
  OrderStatusEnum.Rejected,
];

function formatStatus(status: string | undefined | null): string {
  if (!status) return "";
  return status
    .replace(/[_-]/g, " ")
    .split(" ")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}

function classify(o: OrderOutput): Tab {
  if (o.status === OrderStatusEnum.Draft) return "drafts";
  if (o.status && TERMINAL_STATUSES.includes(o.status)) return "past";
  return "active";
}

function badgeFor(
  o: OrderOutput,
  t: (k: string) => string,
): { tone: "success" | "warn" | "danger" | "neutral"; label: string } {
  switch (o.status) {
    case OrderStatusEnum.Delivered:
      return { tone: "success", label: t("v2.history.status.delivered") };
    case OrderStatusEnum.Completed:
      return { tone: "success", label: t("v2.history.status.completed") };
    case OrderStatusEnum.Cancelled:
      return { tone: "danger", label: t("v2.history.status.cancelled") };
    case OrderStatusEnum.Rejected:
      return { tone: "danger", label: t("v2.history.status.rejected") };
    case OrderStatusEnum.Draft:
      return { tone: "neutral", label: t("v2.history.status.draft") };
    case OrderStatusEnum.OutForDelivery:
      return { tone: "warn", label: t("v2.history.status.out_for_delivery") };
    case OrderStatusEnum.Preparing:
      return { tone: "warn", label: t("v2.history.status.preparing") };
    case OrderStatusEnum.ReadyForPickup:
      return { tone: "warn", label: t("v2.history.status.ready") };
    default:
      return { tone: "neutral", label: formatStatus(o.status) || t("v2.history.status.pending") };
  }
}

function getOrderTypeLabel(type: OrderTypeEnum | null | undefined, t: any) {
  if (!type) return "";
  switch (type) {
    case OrderTypeEnum.Pickup:
      return t("v2.home.pickup");
    case OrderTypeEnum.Delivery:
      return t("v2.home.delivery");
    case OrderTypeEnum.DineIn:
      return t("v2.checkout.dine_in");
    case OrderTypeEnum.Takeaway:
      return t("v2.home.pickup");
    default:
      return formatStatus(type);
  }
}

function getPaymentStatusLabel(status: PaymentStatusEnum | null | undefined, t: any) {
  if (!status) return "";
  switch (status) {
    case PaymentStatusEnum.Paid:
      return t("v2.order_detail.pay_status.paid");
    case PaymentStatusEnum.Unpaid:
      return t("v2.order_detail.pay_status.unpaid");
    case PaymentStatusEnum.PartiallyPaid:
      return t("v2.order_detail.pay_status.partial");
    case PaymentStatusEnum.Refunded:
      return t("v2.order_detail.pay_status.refunded");
    default:
      return formatStatus(status);
  }
}

function getPaymentMethodLabel(method: string | null | undefined, t: any) {
  if (!method) return "";
  const m = method.toLowerCase();
  if (m === "cash") return t("cash");
  if (m === "card") return t("v2.checkout.card_payment");
  return formatStatus(method);
}

function getLineDetails(l: OrderLineOutput, lang: string, menuItem?: UiMenuItem) {
  return {
    name: menuItem?.name || localize(l.itemName, lang) || "",
    image: l.imageUrl,
    quantity: l.quantity,
  };
}

export function HistoryScreen() {
  const navigate = useNavigate();
  const { i18n, t } = useTranslation();
  const { isLoggedIn } = useAuth();
  const { orders, total, loading, hasMore, error, loadMore } = useOrders();
  const { format } = useFormatPrice();
  const { items: menuItems } = useMenu();
  const { cart, addToCart, clearCart } = useCart();
  const [tab, setTab] = useState<Tab>("past");
  const [signInOpen, setSignInOpen] = useState(false);
  const [reorderState, setReorderState] = useState<
    | { kind: "none" }
    | { kind: "empty" }
    | { kind: "confirm"; lines: CartLine[]; skipped: number }
    | { kind: "skipped"; count: number }
  >({ kind: "none" });
  const sentinelRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!sentinelRef.current || !hasMore || loading) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          loadMore();
        }
      },
      { threshold: 0.1, rootMargin: "100px" },
    );

    observer.observe(sentinelRef.current);
    return () => observer.disconnect();
  }, [hasMore, loading, loadMore]);

  const lang = i18n.language || "en";

  const buildReorderLines = (order: OrderOutput) => {
    const lines = order.orderLines ?? [];
    const builtLines: CartLine[] = [];
    let skipped = 0;

    for (const l of lines as OrderLineOutput[]) {
      const menuItem = menuItems.find((m) => m.id === l.itemId);
      if (!menuItem) {
        skipped++;
        console.warn(`[reorder] item ${l.itemId} not in current menu, skipping`);
        continue;
      }
      const selectedModifiers: SelectedModifierOption[] = l.selectedModifiers ?? [];
      const selectedVariants: SelectedVariantOption[] = l.selectedVariants ?? [];

      const modsLabel: string[] = [];
      selectedVariants.forEach((v) => {
        const lbl = localize(v.variantOptionName, lang);
        if (lbl) modsLabel.push(lbl);
      });
      selectedModifiers.forEach((m) => {
        const lbl = localize(m.modifierOptionName, lang);
        if (lbl) modsLabel.push(lbl);
      });

      const modifiersTotal = selectedModifiers.reduce(
        (s, m) => s + (m.unitPrice ?? 0) * (m.quantity ?? 1),
        0,
      );
      const variantAdjust = selectedVariants.reduce(
        (s, v) => s + (v.priceAdjustment ?? 0),
        0,
      );
      const qty = l.quantity ?? 1;
      // Use the recorded per-unit configured price (line subtotal ÷ qty). Re-adding
      // variant/modifier adjustments onto l.unitPrice double-counted them, doubling
      // the reordered price (l.unitPrice is already the configured price).
      const recordedSubtotal = l.pricingSummary?.subtotalAmount;
      const unitPrice =
        recordedSubtotal != null && qty > 0
          ? recordedSubtotal / qty
          : (l.unitPrice ?? menuItem.price + variantAdjust + modifiersTotal);

      builtLines.push({
        id: `${l.itemId}-${Date.now()}-${builtLines.length}`,
        itemId: l.itemId,
        name: menuItem.name,
        categoryId: menuItem.cat,
        categoryName: menuItem.categoryName,
        categoryNameRaw: menuItem.categoryNameRaw,
        price: unitPrice,
        qty,
        tone: menuItem.tone,
        selectedModifiers,
        selectedVariants,
        modsLabel,
        notes: l.notes ?? "",
      });
    }

    return { lines: builtLines, skipped };
  };

  const reorder = (order: OrderOutput) => {
    const { lines: builtLines, skipped } = buildReorderLines(order);
    if (builtLines.length === 0) {
      setReorderState({ kind: "empty" });
      return;
    }
    if (cart.length > 0) {
      setReorderState({ kind: "confirm", lines: builtLines, skipped });
      return;
    }
    builtLines.forEach((line) => addToCart(line));
    if (skipped > 0) {
      setReorderState({ kind: "skipped", count: skipped });
      return;
    }
    navigate("/cart");
  };

  const commitReorder = (lines: CartLine[], skipped: number) => {
    clearCart();
    lines.forEach((line) => addToCart(line));
    setReorderState({ kind: "none" });
    if (skipped > 0) {
      setReorderState({ kind: "skipped", count: skipped });
      return;
    }
    navigate("/cart");
  };

  const dateFmt = useMemo(
    () =>
      new Intl.DateTimeFormat(i18n.language, {
        month: "short",
        day: "numeric",
        year: "numeric",
        hour: "numeric",
        minute: "numeric",
        hour12: true,
      }),
    [i18n.language],
  );

  const filtered = useMemo(
    () => orders.filter((o) => classify(o) === tab),
    [orders, tab],
  );

  const tabs: { id: Tab; label: string }[] = [
    { id: "active", label: t("v2.history.tab_active") },
    { id: "past", label: t("v2.history.tab_past") },
  ];

  if (!isLoggedIn) {
    return (
      <div style={{ paddingBottom: 80 }}>
        <TopBar title={t("v2.history.title")} onBack={() => navigate("/")} />
        <div
          style={{
            padding: "32px 16px",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 12,
            textAlign: "center",
          }}
        >
          <div
            style={{
              width: 56,
              height: 56,
              borderRadius: 999,
              background: "var(--surface-2)",
              display: "grid",
              placeItems: "center",
              color: "var(--ink-2)",
            }}
          >
            <Icons.receipt size={24} />
          </div>
          <div className="display" style={{ fontSize: 16, fontWeight: 700, color: "var(--ink-1)" }}>
            {t("v2.history.sign_in_title")}
          </div>
          <div
            style={{
              fontSize: 13,
              color: "var(--ink-2)",
              maxWidth: 280,
              lineHeight: 1.45,
            }}
          >
            {t("v2.history.sign_in_desc")}
          </div>
          <Button size="lg" onClick={() => setSignInOpen(true)}>
            {t("v2.history.sign_in")}
          </Button>
        </div>
        <SignInModal open={signInOpen} onClose={() => setSignInOpen(false)} />
      </div>
    );
  }

  return (
    <div style={{ paddingBottom: 80 }}>
      <TopBar
        title={`${t("v2.history.title")} (${total})`}
        onBack={() => navigate("/")}
      />
      <div style={{ padding: "8px 16px 12px" }}>
        <div style={{ display: "flex", gap: 6, marginBottom: 14 }}>
          {tabs.map((t) => {
            const on = t.id === tab;
            return (
              <button
                key={t.id}
                onClick={() => setTab(t.id)}
                style={{
                  padding: "6px 14px",
                  borderRadius: "var(--radius-chip)",
                  fontSize: 13,
                  fontWeight: 600,
                  background: on ? "var(--ink-1)" : "var(--surface-2)",
                  color: on ? "#fff" : "var(--ink-2)",
                  border: 0,
                  cursor: "pointer",
                  fontFamily: "inherit",
                }}
              >
                {t.label}
              </button>
            );
          })}
        </div>

        {loading && orders.length === 0 && (
          <div style={{ padding: 24, textAlign: "center", color: "var(--ink-3)" }}>
            {t("v2.history.loading")}
          </div>
        )}

        {error && !loading && (
          <div
            style={{
              fontSize: 13,
              color: "#b91c1c",
              padding: "10px 12px",
              background: "#fee2e2",
              borderRadius: 10,
              marginBottom: 12,
            }}
          >
            {t("v2.history.couldnt_load")} {error.message}
          </div>
        )}

        {!loading && filtered.length === 0 && !error && (
          <div
            style={{
              padding: "24px 12px",
              textAlign: "center",
              color: "var(--ink-2)",
              fontSize: 13,
            }}
          >
            {tab === "active"
              ? t("v2.history.none_active")
              : tab === "drafts"
                ? t("v2.history.none_drafts")
                : t("v2.history.none_past")}
          </div>
        )}

        {reorderState.kind === "empty" && (
          <Card padding={14} style={{ background: "#fef2f2", borderColor: "#fecaca", marginBottom: 12 }}>
            <div style={{ fontSize: 13, color: "#7f1d1d", marginBottom: 10 }}>
              {t("v2.history.reorder_none_available")}
            </div>
            <Button size="sm" variant="outline" onClick={() => setReorderState({ kind: "none" })}>
              {t("v2.common.close")}
            </Button>
          </Card>
        )}
        {reorderState.kind === "confirm" && (
          <Card padding={14} style={{ background: "#fff7ed", borderColor: "#fed7aa", marginBottom: 12 }}>
            <div className="display" style={{ fontSize: 14, fontWeight: 700, marginBottom: 4 }}>
              {t("v2.history.reorder")}
            </div>
            <div style={{ fontSize: 13, color: "var(--ink-2)", marginBottom: 10 }}>
              {t("v2.history.reorder_replace_cart")}
            </div>
            <div style={{ display: "flex", gap: 8 }}>
              <Button size="sm" onClick={() => commitReorder(reorderState.lines, reorderState.skipped)}>
                {t("v2.common.apply")}
              </Button>
              <Button size="sm" variant="ghost" onClick={() => setReorderState({ kind: "none" })}>
                {t("v2.common.close")}
              </Button>
            </div>
          </Card>
        )}
        {reorderState.kind === "skipped" && (
          <Card padding={14} style={{ background: "#fffbeb", borderColor: "#fcd34d", marginBottom: 12 }}>
            <div style={{ fontSize: 13, color: "#92400e", marginBottom: 10 }}>
              {t(
                reorderState.count === 1
                  ? "v2.history.reorder_skipped_one"
                  : "v2.history.reorder_skipped_other",
                { count: reorderState.count },
              )}
            </div>
            <div style={{ display: "flex", gap: 8 }}>
              <Button size="sm" onClick={() => { setReorderState({ kind: "none" }); navigate("/cart"); }}>
                {t("v2.cart.title")}
              </Button>
              <Button size="sm" variant="ghost" onClick={() => setReorderState({ kind: "none" })}>
                {t("v2.common.close")}
              </Button>
            </div>
          </Card>
        )}

        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {filtered.map((o) => {
            const badge = badgeFor(o, t);
            const total =
              o.pricingSummary?.totalAmount ?? o.totalPaid ?? 0;
            const dateStr = o.orderTimestamp
              ? dateFmt.format(new Date(o.orderTimestamp))
              : o.createdAt
                ? dateFmt.format(new Date(o.createdAt))
                : "";
            const orderId = o.id ?? o.orderNumber ?? "";
            return (
              <Card key={orderId} padding={14}>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "flex-start",
                    marginBottom: 10,
                  }}
                >
                  <div>
                    <div className="display" style={{ fontSize: 14, fontWeight: 700 }}>
                      {t("v2.history.order_number", { n: o.orderNumber || orderId })}
                    </div>
                    <div style={{ fontSize: 12, color: "var(--ink-3)", marginTop: 2 }}>
                      {dateStr}
                    </div>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 6,
                        marginTop: 4,
                        fontSize: 12,
                        color: "var(--ink-3)",
                      }}
                    >
                      <span>{getOrderTypeLabel(o.orderType, t)}</span>
                      <span>·</span>
                      <span>{getPaymentMethodLabel(o.selectedPaymentMethodType, t)}</span>
                      <span>·</span>
                      <span
                        style={{
                          color:
                            o.paymentStatus === PaymentStatusEnum.Paid
                              ? "#16a34a"
                              : o.paymentStatus === PaymentStatusEnum.Unpaid
                                ? "#dc2626"
                                : "inherit",
                          fontWeight: 500,
                        }}
                      >
                        {getPaymentStatusLabel(o.paymentStatus, t)}
                      </span>
                    </div>
                  </div>
                  <Badge tone={badge.tone}>
                    {badge.tone === "success" && <Icons.check size={10} />} {badge.label}
                  </Badge>
                </div>
                {(o.orderLines ?? []).length > 0 && (
                  <div
                    style={{
                      display: "flex",
                      gap: 12,
                      overflowX: "auto",
                      marginBottom: 16,
                      paddingBottom: 4,
                      msOverflowStyle: "none",
                                }}
                    className="no-scrollbar"
                  >
                    {(o.orderLines ?? []).map((l, i) => {
                      const menuItem = menuItems.find((m) => m.id === l.itemId);
                      const details = getLineDetails(l, lang, menuItem);
                      return (
                        <div
                          key={i}
                          style={{
                            flex: "0 0 80px",
                            display: "flex",
                            flexDirection: "column",
                            gap: 6,
                          }}
                        >
                          <div style={{ position: "relative" }}>
                            <FoodImg
                              src={details.image}
                              tone={TONES[i % TONES.length]}
                              square
                              rounded={12}
                              style={{ width: 80, height: 80 }}
                            />
                            {details.quantity > 1 && (
                              <div
                                style={{
                                  position: "absolute",
                                  top: 4,
                                  right: 4,
                                  background: "var(--ink-1)",
                                  color: "#fff",
                                  fontSize: 10,
                                  fontWeight: 700,
                                  width: 18,
                                  height: 18,
                                  borderRadius: 99,
                                  display: "grid",
                                  placeItems: "center",
                                  border: "1px solid #fff",
                                }}
                              >
                                {details.quantity}
                              </div>
                            )}
                          </div>
                          <div
                            style={{
                              fontSize: 11,
                              fontWeight: 500,
                              color: "var(--ink-1)",
                              lineHeight: 1.2,
                              display: "-webkit-box",
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: "vertical",
                              overflow: "hidden",
                              textAlign: "center",
                            }}
                          >
                            {details.name}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                  }}
                >
                  <div className="display" style={{ fontSize: 15, fontWeight: 700 }}>
                    <Price amount={Number(total)} />
                  </div>
                  <div style={{ display: "flex", gap: 8 }}>
                    {tab === "active" && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => navigate(`/tracking/${o.id}`)}
                      >
                        {t("v2.history.track_order", "Track Order")}
                      </Button>
                    )}
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => navigate(`/orders/${orderId}`)}
                    >
                      {t("v2.history.receipt")}
                    </Button>
                    <Button variant="primary" size="sm" onClick={() => reorder(o)}>
                      {t("v2.history.reorder")}
                    </Button>
                  </div>
                </div>
                {tab === "past" &&
                  (o.status === OrderStatusEnum.Completed ||
                    o.status === OrderStatusEnum.Delivered) && (
                    <div style={{ textAlign: "end", marginTop: 8 }}>
                      <OrderReviewButton orderId={orderId} />
                    </div>
                  )}
              </Card>
            );
          })}
          {hasMore && (
            <div
              ref={sentinelRef}
              style={{
                height: 40,
                display: "grid",
                placeItems: "center",
                marginTop: 8,
              }}
            >
              {loading && (
                <div style={{ color: "var(--ink-3)", fontSize: 13 }}>
                  {t("v2.history.loading")}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function OrderReviewButton({ orderId }: { orderId: string }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [eligibility, setEligibility] = useState<ReviewEligibility | null>(null);

  useEffect(() => {
    // No customer identity → the BFF's eligibility route 401s unconditionally
    // (see reviewApi.canAttemptReview). Skip the request; render nothing.
    if (!canAttemptReview()) return;
    let active = true;
    reviewApi.getEligibility(orderId).then((res) => {
      if (active) setEligibility(res);
    }).catch((err) => {
      console.warn("Failed to fetch review eligibility for order", orderId, err);
    });
    return () => { active = false; };
  }, [orderId]);

  if (!eligibility) {
    return null; // Don't show anything while loading
  }

  if (eligibility.existingReview) {
    return (
      <button
        type="button"
        onClick={() => navigate(`/orders/${orderId}/review`)}
        style={{
          background: "transparent",
          border: 0,
          padding: 0,
          font: "600 12px inherit",
          color: "var(--ink-3)",
          cursor: "pointer",
          display: "inline-flex",
          alignItems: "center",
          gap: 4,
        }}
      >
        <StarRating value={eligibility.existingReview.rating} readOnly size={13} />
        {t("v2.reviews.entry.view_review", "View review")}
      </button>
    );
  }

  if (eligibility.eligible) {
    return (
      <button
        type="button"
        onClick={() => navigate(`/orders/${orderId}/review`)}
        style={{
          background: "transparent",
          border: 0,
          padding: 0,
          font: "600 12px inherit",
          color: "var(--ink-3)",
          cursor: "pointer",
        }}
      >
        {t("v2.reviews.entry.history_hint", "★ Leave a review")}
      </button>
    );
  }

  return null;
}
